// loggerInit.js - Initialize logger with app context and user information
import logger from './logger';

/**
 * Initialize logger with application context
 */
export function initializeLogger() {
  // Set app context
  logger.info('Pista Mobile App initializing', {
    version: process.env.REACT_APP_VERSION || 'unknown',
    environment: process.env.NODE_ENV || 'development',
    buildTime: process.env.REACT_APP_BUILD_TIME || 'unknown',
    platform: getPlatform(),
    userAgent: navigator.userAgent,
  });

  // Log performance metrics
  if (typeof window !== 'undefined' && window.performance) {
    window.addEventListener('load', () => {
      const navigation = window.performance.timing;
      const loadTime = navigation.loadEventEnd - navigation.navigationStart;

      logger.performance('page_load', loadTime, {
        domContentLoaded: navigation.domContentLoadedEventEnd - navigation.navigationStart,
        firstPaint: window.performance.getEntriesByType('paint')?.[0]?.startTime,
      });
    });
  }

  // Log unhandled promise rejections
  if (typeof window !== 'undefined') {
    window.addEventListener('unhandledrejection', (event) => {
      logger.error('Unhandled promise rejection', {
        reason: event.reason,
        promise: event.promise,
        stack: event.reason?.stack,
      });
    });

    // Log uncaught errors
    window.addEventListener('error', (event) => {
      logger.error('Uncaught error', {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        stack: event.error?.stack,
      });
    });
  }
}

/**
 * Update logger with user context
 */
export function setUserContext(user) {
  logger.setUserContext(user);

  if (user) {
    logger.info('User session started', {
      userId: user.id,
      username: user.username,
    });
  } else {
    logger.info('User session ended');
  }
}

/**
 * Get platform information
 */
function getPlatform() {
  if (typeof window === 'undefined') return 'server';

  const userAgent = navigator.userAgent.toLowerCase();

  if (/android/.test(userAgent)) return 'android';
  if (/iphone|ipad|ipod/.test(userAgent)) return 'ios';
  if (/mobile/.test(userAgent)) return 'mobile';
  if (/tablet/.test(userAgent)) return 'tablet';

  return 'web';
}

/**
 * Log user actions consistently
 */
export function logUserAction(action, details = {}) {
  logger.userAction(action, {
    timestamp: new Date().toISOString(),
    ...details,
  });
}

/**
 * Log component lifecycle events
 */
export function logComponentEvent(componentName, event, details = {}) {
  logger.component(componentName, event, {
    timestamp: new Date().toISOString(),
    ...details,
  });
}

/**
 * Log API calls with consistent format
 */
export function logAPICall(method, url, status, duration, details = {}) {
  logger.api(method, url, status, duration, {
    timestamp: new Date().toISOString(),
    ...details,
  });
}

const loggerInit = {
  initializeLogger,
  setUserContext,
  logUserAction,
  logComponentEvent,
  logAPICall,
};

export default loggerInit;
